/**
 * Demo players — the Load / Remove control. Super admin, demo venues only.
 *
 * The server refuses both actions on anything that is not a demo organization,
 * so this control simply does not draw itself there either. Each action asks
 * once before it runs, and reports the row counts the server returned.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Database, Trash2 } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const sum = (counts) => Object.values(counts || {}).reduce((a, b) => a + Number(b || 0), 0);

export const PatronDemoSeedBlock = ({ orgId, status, onChanged }) => {
    const { t } = useTranslation();
    const [ask, setAsk] = useState('');
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState('');

    if (!status) return null;
    if (!status.demo_org) {
        return (
            <Card data-testid="patron-demo-block">
                <CardContent className="pt-5">
                    <p className="text-sm text-slate-500" data-testid="patron-demo-not-demo">
                        {t('patronCommand.demo.onlyDemo')}
                    </p>
                </CardContent>
            </Card>
        );
    }

    const run = async (what) => {
        setBusy(true);
        setAsk('');
        try {
            const res = await api.post(`/patron/demo-players/${what}`, null,
                { params: { organization_id: orgId } });
            const counts = res.data.counts || {};
            setNote(what === 'load'
                ? (res.data.already_loaded
                    ? t('patronCommand.demo.already')
                    : t('patronCommand.demo.loaded', {
                        players: counts.patron_fans || 0, rows: sum(counts) }))
                : t('patronCommand.demo.removed', { rows: sum(counts) }));
            onChanged();
        } catch (_e) {
            setNote(t('patronCommand.demo.failed'));
        }
        setBusy(false);
    };

    return (
        <Card data-testid="patron-demo-block">
            <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                    <Database size={15} className="text-emerald-500" />
                    {t('patronCommand.demo.title')}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <p className="text-sm text-slate-500">{t('patronCommand.demo.body')}</p>
                {ask ? (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-2"
                         data-testid="patron-demo-confirm">
                        <p className="text-sm font-medium">
                            {t(`patronCommand.demo.confirm${ask === 'load' ? 'Load' : 'Remove'}`)}
                        </p>
                        <div className="flex gap-2">
                            <Button size="sm" disabled={busy} onClick={() => run(ask)}
                                    data-testid="patron-demo-confirm-yes">
                                {busy ? t('patronCommand.demo.working') : t('patronCommand.demo.yes')}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setAsk('')}
                                    data-testid="patron-demo-confirm-cancel">
                                {t('patronCommand.demo.cancel')}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-wrap gap-2">
                        <Button size="sm" disabled={busy} onClick={() => setAsk('load')}
                                data-testid="patron-demo-load">
                            <Database size={14} className="mr-1" />{t('patronCommand.demo.load')}
                        </Button>
                        <Button size="sm" variant="outline" disabled={busy || !status.seeded}
                                onClick={() => setAsk('remove')} data-testid="patron-demo-remove">
                            <Trash2 size={14} className="mr-1" />{t('patronCommand.demo.remove')}
                        </Button>
                    </div>
                )}
                {status.seeded && (
                    <p className="text-xs text-slate-500" data-testid="patron-demo-alex">
                        {t('patronCommand.demo.alex')}
                    </p>
                )}
                {note && (
                    <p className="text-sm font-medium" data-testid="patron-demo-note">{note}</p>
                )}
            </CardContent>
        </Card>
    );
};
